import { useState } from "react";
import { Assessment } from "@/types";
import { apiRequest } from "@/utils/api";
import { API_ENDPOINTS } from "@/config/api";
import { useToast } from "./use-toast";
import { Loader } from "./loader";

interface EditableAssessmentTitleProps {
  assessment: Assessment;
  onTitleUpdated: (newTitle: string) => void;
}

export function EditableAssessmentTitle({ assessment, onTitleUpdated }: EditableAssessmentTitleProps) {
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  const { toast } = useToast();

  const handleTitleEdit = () => {
    if (isEditingTitle) return;
    setIsEditingTitle(true);
    const input = document.createElement("input");
    input.value = assessment.title || "";
    input.className = "text-2xl font-semibold bg-transparent border-none outline-none";
    input.style.width = `${(assessment.title || "").length + 2}ch`;
    const titleSpan = document.getElementById("assessment-title");
    if (titleSpan) {
      titleSpan.replaceWith(input);
      input.focus();
      input.onblur = async () => {
        setIsEditingTitle(false);
        setIsSavingTitle(true);
        try {
          await apiRequest(`${API_ENDPOINTS.assessments.list}/${assessment.id}`, {
            method: "PATCH",
            body: { ...assessment, title: input.value },
          });
          onTitleUpdated(input.value);
        } catch (err: any) {
          toast({
            title: "Error",
            description: err.message,
            variant: "destructive",
          });
        } finally {
          setIsSavingTitle(false);
        }
        const newSpan = document.createElement("span");
        newSpan.id = "assessment-title";
        newSpan.textContent = input.value;
        input.replaceWith(newSpan);
      };
      input.onkeydown = (e) => {
        if (e.key === "Enter") {
          input.blur();
        } else if (e.key === "Escape") {
          const newSpan = document.createElement("span");
          newSpan.id = "assessment-title";
          newSpan.textContent = assessment.title || "";
          input.replaceWith(newSpan);
        }
      };
    }
  };

  return (
    <div className="group relative inline-block cursor-pointer text-2xl font-semibold" onClick={handleTitleEdit}>
      <span id="assessment-title">{assessment?.title}</span>
      {isSavingTitle && (
        <span className="inline-block ml-3">
          <Loader className="w-4 h-4" title="Saving title..." />
        </span>
      )}
      {!isEditingTitle && !isSavingTitle && (
        <span className="invisible group-hover:visible text-xs text-gray-500 ml-2">(click to edit)</span>
      )}
    </div>
  );
}
